/* Copyright (c) 2022 MOCA Systems, Inc. All rights reserved. */

// -------------- ALERT -----------------
// Thinking about updating this file? It's slated to be replaced, so that
// may be a waste of time TP-6557

import { Injectable, OnDestroy } from '@angular/core';
import { Subject, combineLatest, of, Observable, BehaviorSubject, from } from 'rxjs';
import {
	fancyFirebaseGetters,
	fancyFirebaseReferencer,
	UserPreferenceCategories,
} from '@touchplan/fancy-referencer';
import { switchMap, takeUntil, map } from 'rxjs/operators';
import { ReportFormControl } from '../types/ReportFormControl';
import { ReportType } from '../types/ReportType';
import firebase from 'firebase/compat/app';
import { IReportUIPrefs, ReportControlsPrefs, ReportTypesPrefs } from '../types/ReportPrefs';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';

const REPORTS_PREFERENCES_REPORTS_ROOT_NODE = 'reports';

/**
 * Load and store report “options” a.k.a. preferences (i.e. filter, sort-by, group-by, etc.), on a per-user,
 * per-project, per-report-type basis, to:
 * {Root}/userPreferences/{userId}/reportPrefs/{projectId}/reports/{reportType}/{prefName}
 * {Root}/userPreferences/{userId}/reportPrefs/{projectId}/selectedReportType
 * @example
 * ```ts
 * reportsUserPrefsService.reportPrefs$.subscribe(prefs =>
 *     // Use prefs to set values on controls
 * );
 * // Set up initial values
 * reportsUserPrefsService.projectId = ...
 * reportsUserPrefsService.userId = ...
 *
 * // Set up per report type change
 * reportsUserPrefsService.reportType = ...
 * ```
 */
@Injectable({
	providedIn: 'root',
})
export class ReportsUserPrefsService implements OnDestroy {
	private reportRefSubject$ = new BehaviorSubject<firebase.database.Reference | null>(null);
	private reportUIPrefsSubject$ = new BehaviorSubject<IReportUIPrefs | null>(null);

	public reportPrefs$ = this.reportUIPrefsSubject$.asObservable();

	private userId$: BehaviorSubject<string | null> = new BehaviorSubject(null);
	private projectId$: BehaviorSubject<string | null> = new BehaviorSubject(null);
	private shutdownSubject$: Subject<void> = new Subject();
	private logger: ILoggerService;

	constructor() {
		this.logger = LoggerServiceFactory.create('ReportsUserPrefsService');
		// Updating reference observable
		combineLatest([this.userId$, this.projectId$])
			.pipe(
				map(([userId, projectId]) => {
					if (!userId || !projectId) {
						return null;
					}
					const path = fancyFirebaseGetters.getProjectUserPreferencesPath(
						projectId,
						userId,
						UserPreferenceCategories.ReportPrefs
					);
					return fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, projectId);
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.reportRefSubject$);

		// Report preferences object dependent on ref
		this.reportRefSubject$
			.pipe(
				switchMap((ref) => {
					if (ref === null) {
						return of(null);
					}
					return new Observable((subscriber) => {
						ref.on(
							'value',
							(snap) => {
								return subscriber.next(<IReportUIPrefs>snap.val() || {});
							},
							(error) => {
								return subscriber.error(error);
							}
						);
						return () => {
							ref.off('value');
						};
					});
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.reportUIPrefsSubject$);
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	public set userId(uid: string | null) {
		this.userId$.next(uid);
	}

	public set projectId(projId: string) {
		this.projectId$.next(projId);
	}

	private createEmptyPrefs() {
		return {
			selectedReportType: null,
			reports: {} as ReportTypesPrefs,
		} as IReportUIPrefs;
	}

	public setIdsAndPreloadData(uid: string, projId: string): IReportUIPrefs {
		if (uid) {
			this.userId = uid;
		}
		if (projId) {
			this.projectId = projId;
		}
		return this.reportUIPrefsSubject$.value;
	}

	public loadAll(): IReportUIPrefs {
		return this.reportUIPrefsSubject$.value;
	}

	public loadReportType(): ReportType {
		const allPrefs = this.loadAll();
		return allPrefs.selectedReportType as ReportType;
	}

	public storeReportType(reportType: string) {
		const firebaseRef = this.reportRefSubject$.value;
		if (!firebaseRef) {
			this.logger.error('Unexpected null/undefined Firebase reference to user preferences for Reports UI');
			return;
		}
		firebaseRef.child('selectedReportType').set(reportType);
	}

	public load(type: ReportType, control: ReportFormControl) {
		const allPrefs = this.loadAll();
		// Return the value of the specific control, in the given ReportType:
		const prefsForType = allPrefs.reports ? allPrefs.reports[type] : undefined;
		return prefsForType ? prefsForType[control] : undefined;
	}

	public store(type: ReportType, control: ReportFormControl, value: string) {
		const allPrefs = this.reportUIPrefsSubject$.value || this.createEmptyPrefs();
		if (!allPrefs.reports) {
			allPrefs.reports = {} as ReportTypesPrefs;
		}
		if (!allPrefs.reports[type]) {
			allPrefs.reports[type] = {} as ReportControlsPrefs;
		}
		allPrefs.reports[type][control] = value;
		// Store the updated data
		this.reportRefSubject$.value.update(allPrefs);
	}

	/**
	 * Clears saved user preferences for a specified report type.
	 * It effectively removes any previously saved settings specific to that report type.
	 *
	 * @param type - The report type for which preferences should be cleared.
	 * @returns An Observable that completes once the preferences have been successfully cleared.
	 */
	public clearPreferences(type: ReportType): Observable<void> {
		return from(
			this.reportRefSubject$.value?.child(REPORTS_PREFERENCES_REPORTS_ROOT_NODE).child(type).remove()
		);
	}
}
